import { useRef, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * useUniqueToast - Prevents duplicate toast notifications
 * 
 * Problem: Double clicks cause duplicate toasts
 * Solution: Track toast IDs and prevent duplicates within cooldown
 * 
 * @param {number} cooldownMs - Time before same toast can show again (default: 3000)
 * @returns {object} { success, error, info, warning }
 * 
 * Usage:
 * const uniqueToast = useUniqueToast();
 * uniqueToast.success('message-sent', 'Message sent!');
 */
export function useUniqueToast(cooldownMs = 3000) {
  const shownToastsRef = useRef(new Set());

  const showToast = useCallback((type, id, message, options = {}) => {
    if (shownToastsRef.current.has(id)) return null;

    shownToastsRef.current.add(id);
    setTimeout(() => shownToastsRef.current.delete(id), cooldownMs);

    switch (type) {
      case 'success': return toast.success(message, options);
      case 'error': return toast.error(message, options);
      case 'info': return toast.info(message, options);
      case 'warning': return toast.warning(message, options);
      default: return toast(message, options);
    }
  }, [cooldownMs]);

  return {
    success: useCallback((id, msg, opts) => showToast('success', id, msg, opts), [showToast]),
    error: useCallback((id, msg, opts) => showToast('error', id, msg, opts), [showToast]),
    info: useCallback((id, msg, opts) => showToast('info', id, msg, opts), [showToast]),
    warning: useCallback((id, msg, opts) => showToast('warning', id, msg, opts), [showToast]),
  };
}

/**
 * Global toast deduplication - for non-component contexts
 */
const globalShownToasts = new Set();

export function showUniqueToast(type, id, message, cooldownMs = 3000) {
  if (globalShownToasts.has(id)) return null;

  globalShownToasts.add(id);
  setTimeout(() => globalShownToasts.delete(id), cooldownMs);

  switch (type) {
    case 'success': return toast.success(message);
    case 'error': return toast.error(message);
    case 'info': return toast.info(message);
    case 'warning': return toast.warning(message);
    default: return toast(message);
  }
}

export default useUniqueToast;
